import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "./api";
import { useMutationToast } from "../hooks/useMutationToast";

export interface ResumeRow {
  id: string;
  user_id: string;
  label: string;
  r2_pdf_key: string;
  r2_txt_key: string;
  bytes: number;
  uploaded_at: number;
}

export interface ResumesResponse {
  resumes: ResumeRow[];
}

export function useResumes() {
  return useQuery({
    queryKey: ["resumes"],
    queryFn: async () => {
      const res = await fetch("/api/resumes", {
        credentials: "same-origin",
      });
      if (!res.ok) throw new ApiError(res.status, await res.text());
      return (await res.json()) as ResumesResponse;
    },
    staleTime: 30_000,
  });
}

export interface UploadResumeInput {
  label: string;
  file: File;
}

export interface UploadResumeResult {
  resume: ResumeRow;
  text_chars: number;
}

export interface ResumeError {
  status: number;
  error: string;
  hint?: string;
}

// Multipart upload — bypass the JSON helper because the browser must set the
// content-type header (with boundary) itself.
export function useUploadResume() {
  const qc = useQueryClient();
  return useMutationToast<UploadResumeResult, ResumeError, UploadResumeInput>({
    success: (data) => `Uploaded ${data.resume.label} (${data.text_chars.toLocaleString()} chars).`,
    errorPrefix: "Upload failed",
    mutationFn: async ({ label, file }) => {
      const fd = new FormData();
      fd.append("label", label);
      fd.append("file", file, file.name);
      const res = await fetch("/api/resumes", {
        method: "POST",
        credentials: "same-origin",
        body: fd,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw {
          status: res.status,
          error:
            (body as { error?: string }).error ?? `http_${res.status}`,
          hint: (body as { hint?: string }).hint,
        } satisfies ResumeError;
      }
      return body as UploadResumeResult;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["resumes"] });
    },
  });
}

export function useDeleteResume() {
  const qc = useQueryClient();
  return useMutationToast<{ ok: true }, ResumeError, string>({
    success: "Resume deleted.",
    errorPrefix: "Delete failed",
    mutationFn: async (id) => {
      const res = await fetch(`/api/resumes/${id}`, {
        method: "DELETE",
        credentials: "same-origin",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw {
          status: res.status,
          error: (body as { error?: string }).error ?? `http_${res.status}`,
        } satisfies ResumeError;
      }
      return { ok: true } as const;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["resumes"] });
    },
  });
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}
